import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'
import { useEffect, useRef } from 'react'
import { cx, haptic } from '@dorixona/ui'
import { CODE_LENGTH, codeDigits } from '../model'

export interface OtpInputProps {
  value: string
  onChange: (code: string) => void
  /** Метка каждой клетки для диктора: «Цифра 1». */
  digitLabel: (position: number) => string
  invalid?: boolean
  disabled?: boolean
  describedBy?: string
  /** Сразу ставить курсор в первую пустую клетку: экран и есть ввод кода. */
  focusOnShow?: boolean
}

const CELLS = Array.from({ length: CODE_LENGTH }, (_, index) => index)

/**
 * Четыре клетки кода. Ведёт себя как системное поле: цифровая клавиатура,
 * автоподстановка кода из СМС, вставка всего кода в любую клетку, переход
 * вперёд по вводу и назад по Backspace.
 */
export function OtpInput({
  value,
  onChange,
  digitLabel,
  invalid = false,
  disabled = false,
  describedBy,
  focusOnShow = false,
}: OtpInputProps) {
  const cells = useRef<(HTMLInputElement | null)[]>([])

  // Ошибка — встряска и отдача: палец и глаз узнают об одном и том же сразу.
  useEffect(() => {
    if (invalid) haptic('error')
  }, [invalid])

  const focusCell = (index: number) => {
    cells.current[Math.min(Math.max(index, 0), CODE_LENGTH - 1)]?.focus()
  }

  // Открыли экран — клавиатура сразу под пальцем. Проверка кода выключает
  // клетки и снимает с них фокус; код не подошёл — возвращаем курсор на место.
  useEffect(() => {
    if (focusOnShow && !disabled) focusCell(value.length)
    // Только при появлении и включении клеток: по вводу курсор ведёт setDigits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOnShow, disabled])

  const setDigits = (next: string, caret: number) => {
    onChange(next)
    focusCell(caret)
  }

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const typed = codeDigits(event.target.value)
    if (!typed) return
    // Автоподстановка из СМС приходит целиком в одну клетку.
    if (typed.length > 1) {
      setDigits(typed, typed.length)
      return
    }
    const next = (value.slice(0, index) + typed + value.slice(index + 1)).slice(0, CODE_LENGTH)
    setDigits(next, index + 1)
  }

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace') return
    event.preventDefault()
    // Пустая клетка — стираем предыдущую цифру и уходим назад.
    const target = value[index] ? index : index - 1
    if (target < 0) return
    setDigits(value.slice(0, target) + value.slice(target + 1), target)
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = codeDigits(event.clipboardData.getData('text'))
    if (!pasted) return
    event.preventDefault()
    setDigits(pasted, pasted.length)
  }

  return (
    <div className={cx('otp-row', invalid && 'is-invalid')} aria-describedby={describedBy}>
      {CELLS.map((index) => (
        <input
          key={index}
          ref={(node) => {
            cells.current[index] = node
          }}
          type="text"
          inputMode="numeric"
          // Только первая клетка объявляет одноразовый код — иначе система
          // предлагает подстановку в каждую.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={CODE_LENGTH}
          disabled={disabled}
          aria-label={digitLabel(index + 1)}
          aria-invalid={invalid}
          value={value[index] ?? ''}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  )
}
